import React, { useEffect, useState } from "react";
import { Button, Checkbox, Spin, Tooltip, Typography } from "antd";
import { FileOutlined } from "@ant-design/icons";
import { isMobile } from "react-device-detect";

import OptionList from "../OptionList";
import { useSearchContext } from "../contexts/searchcontext.ts";
import { SaveToFavorites } from "../favorites/SaveToFavorites";
import { FAVORITE_TYPES, SAVED_SEARCH_TYPES } from "../../globals";
import { useModalContext } from "../contexts/modalcontext";

const OPTIONS_LIMIT_BY_ENTITY = {
  [SAVED_SEARCH_TYPES.PLAYER_CHARTS]: {
    players: 7,
    setNameYears: 7,
    grades: 7,
    variations: 7,
  },
  [SAVED_SEARCH_TYPES.SEALED_WAX_CHARTS]: {
    sports: 2,
    setNameYears: 7,
    boxTypes: 7,
  },
  [SAVED_SEARCH_TYPES.SET_AND_YEAR]: {
    sports: 1,
    players: 7,
    sets: 2,
    years: 2,
    grades: 2,
    variations: 2,
  },
};

const CascadingPanel = ({
  searchFields,
  requiredCount,
  currentStepIndex,
  field,
  queryParams,
  dataChanged,
  gotToNextStep,
  goEdit,
  savedSearchType,
  rcOnly,
  setRcOnly,
  isWizardLoading,
}) => {
  const fieldSelected = `${field}Selected`;
  const [loadingOnEditClick, setLoadingOnEditClick] = useState(false);
  const searchContext = useSearchContext();
  const fieldIndex = searchFields.indexOf(field);
  const isRequired = fieldIndex < requiredCount;
  const hideByDefault = savedSearchType === SAVED_SEARCH_TYPES.SET_AND_YEAR ? false : field === "players";
  const hideSearch = savedSearchType === SAVED_SEARCH_TYPES.SET_AND_YEAR ? true : field !== "players";
  const nSelected = searchContext[fieldSelected].length;
  const limit = OPTIONS_LIMIT_BY_ENTITY[savedSearchType][field];

  const isOptionListFilterable = fieldIndex > 0 || savedSearchType === SAVED_SEARCH_TYPES.SEALED_WAX_CHARTS;

  const { setPlayerNotesData, togglePlayerNotesModal } = useModalContext();

  const isPreviousStep = (step) => {
    return searchFields.indexOf(step) < currentStepIndex;
  };

  const isNextHidden = (step) => {
    return searchFields.indexOf(step) !== currentStepIndex;
  };

  const parseTitle = (field) => {
    switch (field) {
      case "setNameYears":
        return "Sets";
      case "boxTypes":
        return "Box Types";
      default: {
        let str = field.replace(/^\w/, (c) => c.toUpperCase());
        if (savedSearchType === SAVED_SEARCH_TYPES.SET_AND_YEAR) str = str.slice(0, -1);
        return str;
      }
    }
  };

  const onEditClick = () => {
    setLoadingOnEditClick(true);
    goEdit(fieldIndex);
  };

  useEffect(() => {
    if (currentStepIndex !== fieldIndex) {
      return;
    }
    const selected = searchContext[field + "Available"].filter(({ value: availableValue }) =>
      searchContext[field + "Selected"].some(({ value: selectedValue }) => availableValue === selectedValue),
    );
    setLoadingOnEditClick(false);
    dataChanged(field, selected, searchContext);
  }, [currentStepIndex]);

  return (
    <div
      key={field}
      className={isPreviousStep(field) ? "prev-step" : isNextHidden(field) ? "transparent" : ""}
      style={{ padding: 12, borderBottom: "1px solid #dadada" }}
    >
      <div style={{ marginBottom: 6 }}>
        <Typography.Text strong>{parseTitle(field)}</Typography.Text>

        {fieldIndex < currentStepIndex && (
          <Typography.Text style={{ float: "right", marginLeft: 12 }}>
            <a type={"primary"} onClick={onEditClick}>
              Edit
            </a>
          </Typography.Text>
        )}

        {loadingOnEditClick && <Spin size="small" style={{ display: "flex", justifyContent: "center" }} />}

        {field === "setNameYears" && savedSearchType === SAVED_SEARCH_TYPES.PLAYER_CHARTS && (
          <div style={{ float: "right", marginLeft: 8, userSelect: "none" }}>
            <Typography.Text style={{ marginRight: 4, fontWeight: 500 }}>RC Only</Typography.Text>
            <Checkbox
              title="RC Only"
              disabled={currentStepIndex !== searchFields.indexOf("setNameYears")}
              checked={rcOnly}
              onChange={() => setRcOnly(!rcOnly)}
            />
          </div>
        )}

        {!isPreviousStep(field) && limit && nSelected >= limit && (
          <Typography.Text style={{ float: "right", fontSize: 12 }}>Max # selected</Typography.Text>
        )}
      </div>

      <div hidden={!isNextHidden(field) || loadingOnEditClick}>
        <ul style={{ marginBottom: 0 }}>
          {searchContext[fieldSelected].map((item) => {
            if (field === "players") {
              return (
                <li key={item.value}>
                  {item.label}
                  <div className="action-button-container" style={{ marginLeft: 6 }}>
                    <SaveToFavorites
                      className="card-action-icon"
                      type={FAVORITE_TYPES.PLAYER}
                      foreignId={item.value}
                      abbreviated="true"
                    />
                    {Array.isArray(item.notes) && item.notes.length > 0 && (
                      <Tooltip trigger={isMobile ? "click" : "hover"} title="Show player notes">
                        <Button
                          className="card-action-icon"
                          type="link"
                          onClick={() => {
                            setPlayerNotesData(item.notes);
                            togglePlayerNotesModal();
                          }}
                          icon={<FileOutlined />}
                        />
                      </Tooltip>
                    )}
                  </div>
                </li>
              );
            }

            return <li key={item.label}>{item.label}</li>;
          })}

          {!searchContext[fieldSelected].length && <li style={{ fontStyle: "italic" }}>No selection(s) made.</li>}
        </ul>
      </div>

      <div hidden={isNextHidden(field)}>
        <OptionList
          limit={limit}
          nSelected={nSelected}
          heading={field}
          fieldName={field}
          hideByDefault={hideByDefault}
          hideSearch={hideSearch}
          selectionsChanged={dataChanged}
          filterable={isOptionListFilterable}
          required={isRequired}
          queryParams={queryParams}
          type={
            field === "years" || (field === "players" && savedSearchType === SAVED_SEARCH_TYPES.SET_AND_YEAR)
              ? "select"
              : ""
          }
          maxSelectVisible={field === "players" && savedSearchType === SAVED_SEARCH_TYPES.SET_AND_YEAR ? 5 : undefined}
        />
      </div>

      <Button
        block
        type="primary"
        onClick={gotToNextStep}
        disabled={(isRequired && searchContext[fieldSelected].length === 0) || isWizardLoading}
        hidden={isNextHidden(field)}
      >
        {isWizardLoading ? "Loading..." : `Choose ${parseTitle(field)}`}
      </Button>
    </div>
  );
};

export default CascadingPanel;
